/**
 * BackControl: the one back control every screen draws.
 *
 * Rule: a back control returns to the page the person came from when
 * Olive knows it, else to that screen's parent; never to an unrelated
 * page. Olive knows where the person came from in two ways: a page the
 * screen itself remembers (the Directory search this tab last left), or
 * the browser's history when the visit came from inside Olive (an
 * internal referrer with history behind it). A reload, a deep link or an
 * outside referrer has neither, and lands on the parent.
 *
 * A screen whose history is not where the person came from (a Directory
 * detail reached through another detail) turns the history walk off and
 * keeps its remembered page. The control reads the destination's name,
 * so the person knows where it goes before pressing it.
 */
import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Button } from '@/components/primitives/Button';

export interface BackControlProps {
  /** The destination's name, as the control reads ("Home", "Invoices"). */
  label: string;
  /** The screen's parent: where the person is taken when Olive does not
   *  know where they came from. */
  to: string;
  /** The page this screen remembers; when it answers, the person came
   *  from there. */
  remembered?: () => string | null;
  /** Walk the browser's history when the visit came from inside Olive.
   *  Default true. */
  walkHistory?: boolean;
  /** The accessible name ("Back to home"). */
  ariaLabel: string;
  testId?: string;
  className?: string;
}

export type BackDestination = { kind: 'navigate'; to: string } | { kind: 'history' };

/** Where the control goes, from what Olive knows. Pure, so a test can
 *  walk every branch. */
export function backDestination(
  o: { to: string; remembered?: () => string | null; walkHistory?: boolean },
  env: { referrer: string; origin: string; historyLength: number },
): BackDestination {
  const remembered = o.remembered?.() ?? null;
  if (remembered !== null) return { kind: 'navigate', to: remembered };
  const internal = env.referrer !== '' && env.referrer.startsWith(env.origin);
  if (o.walkHistory !== false && internal && env.historyLength > 1) return { kind: 'history' };
  return { kind: 'navigate', to: o.to };
}

export function BackControl({
  label,
  to,
  remembered,
  walkHistory = true,
  ariaLabel,
  testId = 'back-control',
  className,
}: BackControlProps): ReactNode {
  const navigate = useNavigate();
  const onBack = (): void => {
    const d = backDestination(
      { to, remembered, walkHistory },
      { referrer: document.referrer, origin: window.location.origin, historyLength: window.history.length },
    );
    if (d.kind === 'history') window.history.back();
    else navigate(d.to);
  };
  return (
    <Button
      variant="ghost"
      size="sm"
      icon={<ArrowLeft size={16} aria-hidden="true" />}
      onClick={onBack}
      aria-label={ariaLabel}
      data-testid={testId}
      className={className}
    >
      {label}
    </Button>
  );
}
